"use client"

import Image from "next/image"
import { motion, useScroll, useTransform, type MotionValue } from "framer-motion"
import { Activity, BadgeCheck, Code2, Cpu, Database, GitBranch, Gauge, PlugZap, ShieldCheck, Workflow } from "lucide-react"
import { useRef } from "react"

import { cn } from "@/lib/utils"

export type EnvironmentStoryFrame = {
  label: string
  title: string
  description: string
  metric?: string
}

export type PrecisionBentoItem = {
  title: string
  description: string
  label?: string
  size?: "compact" | "wide" | "tall"
}

export type AgentEnvironmentZoomStoryIslandProps = {
  eyebrow?: string
  title: string
  description?: string
  frames: EnvironmentStoryFrame[]
  backgroundImage?: { src: string; alt: string }
  className?: string
}

export type AgentPrecisionBentoIslandProps = {
  eyebrow?: string
  title: string
  description?: string
  items: PrecisionBentoItem[]
  className?: string
}

export function AgentPrecisionBentoIsland({ eyebrow, title, description, items, className }: AgentPrecisionBentoIslandProps) {
  const sectionRef = useRef<HTMLElement>(null)
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end center"] })
  const thesisOpacity = useTransform(scrollYProgress, [0, 0.2, 0.46], [1, 0.32, 0])
  const thesisY = useTransform(scrollYProgress, [0, 0.46], [0, -90])
  const thesisScale = useTransform(scrollYProgress, [0, 0.46], [1, 0.94])
  const cardY = useTransform(scrollYProgress, [0, 0.2], [70, 0])

  const icons = [Cpu, ShieldCheck, Activity, PlugZap, Database, Code2]

  return (
    <section ref={sectionRef} className={cn("relative overflow-clip bg-foreground text-background lg:min-h-[150vh]", className)}>
      <div aria-hidden="true" className="absolute inset-0 bg-[radial-gradient(circle_at_50%_12%,hsl(var(--primary)/0.16),transparent_28%),linear-gradient(180deg,transparent_0%,hsl(var(--foreground))_84%)]" />
      <div className="mx-auto w-full max-w-theme px-4 py-20 sm:px-6 sm:py-24 lg:px-8 lg:py-0">
        <motion.div style={{ opacity: thesisOpacity, y: thesisY, scale: thesisScale }} className="relative z-10 mx-auto max-w-4xl text-center lg:pointer-events-none lg:sticky lg:top-0 lg:flex lg:min-h-screen lg:flex-col lg:items-center lg:justify-center">
          {eyebrow && <p className="mb-5 text-sm font-medium uppercase tracking-[0.22em] text-primary">{eyebrow}</p>}
          <h2 className="font-heading text-5xl font-light leading-tight sm:text-6xl lg:text-7xl">{title}</h2>
          {description && <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-background/68">{description}</p>}
        </motion.div>
        <motion.div style={{ y: cardY }} className="relative z-20 mt-12 grid auto-rows-[minmax(13rem,auto)] gap-4 lg:-mt-[34vh] lg:grid-cols-4 lg:pb-32">
          {items.map((item, index) => {
            const Icon = icons[index % icons.length]
            return (
              <article key={item.title} className={cn("group relative flex h-full min-h-[13rem] overflow-hidden rounded-panel border border-background/22 bg-foreground/92 p-5 shadow-[0_26px_90px_rgba(0,0,0,0.28)] backdrop-blur-xl transition-all duration-500 hover:-translate-y-1 hover:border-background/36 hover:bg-foreground/96", item.size === "wide" && "lg:col-span-2", item.size === "tall" && "lg:row-span-2")}>
                <div aria-hidden="true" className="absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100 bg-[radial-gradient(circle_at_18%_0%,hsl(var(--primary)/0.28),transparent_30%)]" />
                <div className="relative z-10 flex h-full flex-col">
                  <div className="flex items-center justify-between gap-4">
                    <span className="flex size-11 items-center justify-center rounded-xl border border-background/22 bg-background/10 text-primary"><Icon className="size-5" /></span>
                    {item.label && <span className="rounded-full bg-background/10 px-3 py-1 text-xs text-background/70">{item.label}</span>}
                  </div>
                  <div className="mt-auto pt-10">
                    <h3 className="text-xl font-medium text-background">{item.title}</h3>
                    <p className="mt-3 text-sm leading-6 text-background/72">{item.description}</p>
                  </div>
                </div>
              </article>
            )
          })}
        </motion.div>
      </div>
    </section>
  )
}

function GeneratedEnvironment({ scale, y, opacity, progress }: { scale: MotionValue<number>; y: MotionValue<number>; opacity: MotionValue<number>; progress: MotionValue<number> }) {
  const dayOpacity = useTransform(progress, [0, 0.45, 0.8], [1, 0.72, 0])
  const duskOpacity = useTransform(progress, [0.18, 0.55, 0.88], [0, 1, 0.2])
  const nightOpacity = useTransform(progress, [0.58, 1], [0, 1])
  const sunY = useTransform(progress, [0, 1], [0, 170])
  const moonY = useTransform(progress, [0, 1], [150, 0])
  const horizonScale = useTransform(progress, [0, 1], [0.72, 1.16])

  return (
    <motion.div aria-hidden="true" style={{ scale, y, opacity }} className="absolute inset-0 origin-center overflow-hidden">
      <motion.div style={{ opacity: dayOpacity }} className="absolute inset-0 bg-[radial-gradient(circle_at_52%_16%,hsl(var(--background)/0.96),transparent_19%),radial-gradient(circle_at_20%_34%,hsl(var(--primary)/0.18),transparent_24%),linear-gradient(180deg,hsl(var(--background))_0%,hsl(var(--muted))_42%,hsl(var(--primary)/0.14)_72%,hsl(var(--foreground)/0.12)_100%)]" />
      <motion.div style={{ opacity: duskOpacity }} className="absolute inset-0 bg-[radial-gradient(circle_at_50%_20%,hsl(var(--primary)/0.24),transparent_22%),linear-gradient(180deg,hsl(var(--background)/0.92)_0%,hsl(var(--primary)/0.2)_34%,hsl(var(--foreground)/0.2)_72%,hsl(var(--foreground)/0.52)_100%)]" />
      <motion.div style={{ opacity: nightOpacity }} className="absolute inset-0 bg-[radial-gradient(circle_at_70%_18%,hsl(var(--background)/0.36),transparent_11%),radial-gradient(circle_at_24%_30%,hsl(var(--primary)/0.16),transparent_24%),linear-gradient(180deg,hsl(var(--foreground)/0.46)_0%,hsl(var(--foreground)/0.78)_64%,hsl(var(--foreground))_100%)]" />
      <motion.div style={{ y: sunY, opacity: dayOpacity }} className="absolute left-[48%] top-[12%] size-28 rounded-full bg-background/80 shadow-[0_0_90px_42px_hsl(var(--background)/0.44)]" />
      <motion.div style={{ y: moonY, opacity: nightOpacity }} className="absolute right-[18%] top-[16%] size-20 rounded-full bg-background/74 shadow-[0_0_80px_34px_hsl(var(--background)/0.2)]" />
      <motion.div style={{ scale: horizonScale }} className="absolute inset-x-[-10%] bottom-[36%] h-[24%] origin-bottom bg-[linear-gradient(148deg,transparent_0%,transparent_33%,hsl(var(--background)/0.7)_34%,hsl(var(--muted)/0.88)_50%,hsl(var(--primary)/0.22)_72%,transparent_73%)]" />
      <motion.div style={{ scale: horizonScale }} className="absolute inset-x-[-14%] bottom-[29%] h-[26%] origin-bottom bg-[linear-gradient(166deg,transparent_0%,transparent_38%,hsl(var(--foreground)/0.16)_39%,hsl(var(--primary)/0.18)_68%,transparent_69%)]" />
      <div className="absolute left-[8%] right-[8%] top-[35%] h-px bg-background/46 shadow-[0_0_70px_28px_hsl(var(--background)/0.22)]" />
      <div className="absolute inset-x-[-8%] bottom-0 h-[42%] bg-[linear-gradient(180deg,hsl(var(--background)/0.14),hsl(var(--foreground)/0.28)),repeating-linear-gradient(90deg,hsl(var(--background)/0.18)_0_1px,transparent_1px_88px),repeating-linear-gradient(180deg,transparent_0_32px,hsl(var(--background)/0.07)_32px_33px)]" />
      <div className="absolute inset-x-[12%] bottom-[11%] h-[18%] rounded-[50%] bg-[radial-gradient(ellipse_at_center,hsl(var(--background)/0.34),transparent_66%)] blur-sm" />
      <div className="absolute inset-x-0 bottom-0 h-[24%] bg-[linear-gradient(180deg,transparent,hsl(var(--foreground)/0.5))]" />
    </motion.div>
  )
}

function SceneColorGrade({ progress }: { progress: MotionValue<number> }) {
  const dawnOpacity = useTransform(progress, [0, 0.42, 0.72], [0.28, 0.16, 0])
  const duskOpacity = useTransform(progress, [0.18, 0.54, 0.88], [0, 0.34, 0.08])
  const nightOpacity = useTransform(progress, [0.58, 1], [0, 0.48])

  return (
    <>
      <motion.div aria-hidden="true" style={{ opacity: dawnOpacity }} className="absolute inset-0 bg-[radial-gradient(circle_at_48%_16%,hsl(var(--background)/0.52),transparent_24%),linear-gradient(180deg,hsl(var(--background)/0.2),transparent_58%)]" />
      <motion.div aria-hidden="true" style={{ opacity: duskOpacity }} className="absolute inset-0 bg-[radial-gradient(circle_at_50%_24%,hsl(var(--primary)/0.34),transparent_28%),linear-gradient(180deg,hsl(var(--primary)/0.16),hsl(var(--foreground)/0.46))]" />
      <motion.div aria-hidden="true" style={{ opacity: nightOpacity }} className="absolute inset-0 bg-[radial-gradient(circle_at_72%_18%,hsl(var(--background)/0.22),transparent_13%),linear-gradient(180deg,hsl(var(--foreground)/0.32),hsl(var(--foreground)/0.72))]" />
    </>
  )
}

function StoryFrameCard({ frame, index, total, progress }: { frame: EnvironmentStoryFrame; index: number; total: number; progress: MotionValue<number> }) {
  const start = index / total
  const middle = (index + 0.5) / total
  const end = (index + 1) / total
  const opacity = useTransform(progress, [Math.max(0, start - 0.14), middle, Math.min(1, end + 0.14)], [0.22, 1, 0.22])
  const icons = [Workflow, GitBranch, Gauge]
  const Icon = icons[index % icons.length]

  return (
    <motion.article style={{ opacity }} className="rounded-card border border-background/14 bg-background/10 p-4 backdrop-blur-xl">
      <div className="flex items-center justify-between gap-3">
        <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.18em] text-background/72"><Icon className="size-4" />{frame.label}</span>
        {frame.metric && <span className="shrink-0 rounded-full bg-background/14 px-3 py-1 text-xs text-background/74">{frame.metric}</span>}
      </div>
      <h3 className="mt-4 text-base font-medium leading-snug text-background">{frame.title}</h3>
      <p className="mt-2 line-clamp-2 text-xs leading-5 text-background/62">{frame.description}</p>
    </motion.article>
  )
}

function ProductFocusPanel({ scale, y, opacity }: { scale: MotionValue<number>; y: MotionValue<number>; opacity: MotionValue<number> }) {
  return (
    <motion.div style={{ scale, y, opacity }} className="relative overflow-hidden rounded-panel border border-background/18 bg-background/14 p-4 shadow-[0_44px_140px_rgba(0,0,0,0.38)] backdrop-blur-2xl">
      <div className="flex items-center justify-between gap-3 border-b border-background/12 pb-4 text-background/68">
        <span className="text-xs font-medium uppercase tracking-[0.18em]">live agent release</span>
        <span className="rounded-full bg-emerald-300/18 px-3 py-1 text-xs text-emerald-100">healthy</span>
      </div>
      <div className="grid gap-4 pt-4 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="rounded-card bg-background/12 p-4">
          <div className="mb-6 h-3 w-28 rounded-full bg-background/28" />
          <div className="space-y-3">
            {["Evaluation gate passed", "Prompt diff approved", "Rollback snapshot stored"].map((item) => (
              <div key={item} className="flex items-center gap-3 rounded-xl bg-background/12 px-3 py-3 text-sm text-background/78">
                <BadgeCheck className="size-4 text-emerald-200" />
                {item}
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-card bg-foreground/28 p-4">
          <div className="h-32 rounded-xl bg-[linear-gradient(180deg,hsl(var(--background)/0.22),hsl(var(--background)/0.06)),repeating-linear-gradient(90deg,transparent_0_20px,hsl(var(--background)/0.1)_20px_21px)]" />
          <div className="mt-4 grid grid-cols-3 gap-2">
            {["96", "42ms", "$0.08"].map((value) => <span key={value} className="rounded-lg bg-background/12 px-2 py-3 text-center text-sm text-background/82">{value}</span>)}
          </div>
        </div>
      </div>
    </motion.div>
  )
}

export function AgentEnvironmentZoomStoryIsland({ eyebrow, title, description, frames, backgroundImage, className }: AgentEnvironmentZoomStoryIslandProps) {
  const sectionRef = useRef<HTMLElement>(null)
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end end"] })
  const environmentScale = useTransform(scrollYProgress, [0, 0.58, 1], [1, 1.48, 1.92])
  const environmentY = useTransform(scrollYProgress, [0, 1], [0, -210])
  const environmentOpacity = useTransform(scrollYProgress, [0, 0.86, 1], [1, 1, 0.62])
  const productScale = useTransform(scrollYProgress, [0.12, 0.58, 0.92], [0.7, 0.9, 0.98])
  const productY = useTransform(scrollYProgress, [0.12, 0.7, 1], [110, 0, 0])
  const productOpacity = useTransform(scrollYProgress, [0.05, 0.22, 0.92, 1], [0, 1, 1, 0.82])
  const headerOpacity = useTransform(scrollYProgress, [0, 0.34, 0.84], [1, 1, 0.64])
  const headerY = useTransform(scrollYProgress, [0, 0.84], [0, -34])
  const progressScale = useTransform(scrollYProgress, [0, 1], [0.08, 1])

  return (
    <section ref={sectionRef} className={cn("relative h-[420vh] bg-foreground text-background", className)}>
      <div className="sticky top-0 h-screen overflow-hidden">
        {backgroundImage ? (
          <motion.div aria-hidden="true" style={{ scale: environmentScale, y: environmentY, opacity: environmentOpacity }} className="absolute inset-0 origin-center">
            <Image src={backgroundImage.src} alt={backgroundImage.alt} fill sizes="100vw" className="object-cover" priority={false} />
          </motion.div>
        ) : (
          <GeneratedEnvironment scale={environmentScale} y={environmentY} opacity={environmentOpacity} progress={scrollYProgress} />
        )}
        {backgroundImage && <SceneColorGrade progress={scrollYProgress} />}
        <div aria-hidden="true" className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.08),rgba(0,0,0,0.66)),linear-gradient(90deg,rgba(0,0,0,0.7),rgba(0,0,0,0.12)_52%,rgba(0,0,0,0.5))]" />
        <div className="absolute inset-0 mx-auto grid w-full max-w-theme items-center gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[0.82fr_1.18fr] lg:px-8">
          <motion.div style={{ opacity: headerOpacity, y: headerY }} className="relative z-10 max-w-2xl self-center">
            {eyebrow && <p className="mb-5 text-sm font-medium uppercase tracking-[0.22em] text-background/68">{eyebrow}</p>}
            <h2 className="font-heading text-4xl font-light leading-[1.02] text-background sm:text-6xl lg:text-7xl">{title}</h2>
            {description && <p className="mt-6 max-w-xl text-base leading-7 text-background/72 sm:text-lg">{description}</p>}
            <div className="mt-8 h-1 w-52 overflow-hidden rounded-full bg-background/18"><motion.div style={{ scaleX: progressScale }} className="h-full origin-left rounded-full bg-background" /></div>
          </motion.div>
          <div className="relative z-10 grid gap-10 self-center">
            <ProductFocusPanel scale={productScale} y={productY} opacity={productOpacity} />
            <div className="hidden gap-3 lg:grid lg:grid-cols-4">
              {frames.slice(0, 4).map((frame, index) => <StoryFrameCard key={frame.title} frame={frame} index={index} total={Math.max(frames.slice(0, 4).length, 1)} progress={scrollYProgress} />)}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
